import { api } from '../lib/api';
import type { AiUsage } from '../types/ai';
import type { Backlink, EntityAttachment, EntityDocument, EntitySummary, EntityTag } from '../types/entities';
import type { SceneConflict, SceneSegment, SceneSegmentField } from '../types/scenes';
import type { WorkspaceSummary } from '../types/workspace';

type WorkspaceEntityActionsStore = {
  workspace: WorkspaceSummary | null;
  entities: EntitySummary[];
  activeEntity: EntityDocument | null;
  activeEntityTags: EntityTag[];
  backlinks: Backlink[];
  sceneSegments: SceneSegment[];
  sceneSegmentFields: SceneSegmentField[];
  sceneConflicts: SceneConflict[];
  error: string;
  loadEntities(): Promise<void>;
  loadEntityTags(entityId?: string): Promise<void>;
  loadBacklinks(entityId?: string): Promise<void>;
  loadSceneSegments(sceneId?: string): Promise<void>;
  loadCategoryCollections(): Promise<void>;
  loadExplorer(): Promise<void>;
  loadTimeline(): Promise<void>;
  scheduleStoryTimelineRefresh(): void;
  refreshConfiguration(): Promise<void>;
  loadEntityFieldHistory(entityId?: string): Promise<void>;
  loadResolvedEntityFieldValues(entityId?: string): Promise<void>;
  clearEntityTabCache(): void;
};

export const workspaceEntityActions = {
  async updateEntityFields(this: WorkspaceEntityActionsStore, entityId: string, input: { fields: Array<Record<string, unknown>>; baseRevision: number }) {
    const entity = await api<EntityDocument>(`/api/v1/entities/${entityId}/fields`, { method: 'PUT', body: JSON.stringify(input) });
    this.clearEntityTabCache();
    if (this.activeEntity?.id === entityId) {
      this.activeEntity = entity;
      await Promise.all([this.loadEntityFieldHistory(entityId), this.loadResolvedEntityFieldValues(entityId), this.loadBacklinks(entityId)]);
    }
    await this.loadEntities();
    return entity;
  },
  async generateAiSummary(this: WorkspaceEntityActionsStore, entityId?: string, targetWords = 100, model: 'luna' | 'terra' | 'sol' = 'luna', confirmedUpgrade = false) {
    const id = entityId ?? this.activeEntity?.id;
    if (!id) return;
    const result = await api<{ summary: string; updatedAt: string; model: string; usage?: AiUsage | null }>(`/api/v1/entities/${id}/ai-summary`, { method: 'POST', body: JSON.stringify({ targetWords, model, confirmedUpgrade }) });
    if (this.activeEntity?.id === id) {
      this.activeEntity.aiSummary = result.summary;
      this.activeEntity.aiSummaryUpdatedAt = result.updatedAt;
    }
    const entity = this.entities.find((item) => item.id === id);
    if (entity) entity.aiSummary = result.summary;
    await this.loadExplorer();
    return result;
  },
  async getAiSummaryPrompt(this: WorkspaceEntityActionsStore, entityId?: string, targetWords = 100) {
    const id = entityId ?? this.activeEntity?.id;
    if (!id) throw new Error('Keine Entity geöffnet.');
    return api<{ prompt: string }>(`/api/v1/entities/${id}/ai-summary-prompt?targetWords=${encodeURIComponent(targetWords)}`);
  },
  async loadBacklinks(this: WorkspaceEntityActionsStore, entityId?: string) {
    entityId ??= this.activeEntity?.id;
    if (!entityId) {
      this.backlinks = [];
      return;
    }
    const entity = this.activeEntity;
    const response = await api<{ items: Backlink[] }>(`/api/v1/entities/${entityId}/backlinks`);
    if (this.activeEntity === entity && entity?.id === entityId) this.backlinks = response.items;
  },
  async createEntity(this: WorkspaceEntityActionsStore, templateId: string, title: string, fieldValues: Record<string, unknown> = {}, explorerParentId?: string | null, categoryCollectionIds: string[] = [], openAfterCreate = true) {
    if (!this.workspace) return;
    this.error = '';
    try {
      const entity = await api<EntityDocument>(`/api/v1/workspaces/${this.workspace.id}/entities`, {
        method: 'POST',
        body: JSON.stringify({ templateId, title, contentDoc: { type: 'doc', content: [] }, fieldValues, categoryCollectionIds, ...(explorerParentId !== undefined ? { explorerParentId } : {}) })
      });
      this.entities = [...this.entities.filter((item) => item.id !== entity.id), entity];
      if (entity.templateCategory === 'szene' || entity.templateCategory === 'kapitel') this.scheduleStoryTimelineRefresh();
      if (openAfterCreate) {
        this.activeEntity = entity; this.backlinks = []; this.activeEntityTags = []; this.sceneSegments = []; this.sceneSegmentFields = []; this.sceneConflicts = [];
        history.pushState({}, '', `/w/${this.workspace.id}/e/${entity.id}`);
        void Promise.all([this.loadEntityTags(entity.id), this.loadBacklinks(entity.id), entity.templateCategory === 'szene' ? this.loadSceneSegments(entity.id) : Promise.resolve()]);
      }
      void Promise.all([this.loadEntities(), this.loadCategoryCollections(), this.loadExplorer()]);
      return entity;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Entity konnte nicht angelegt werden.';
      throw error;
    }
  },
  async createFreeEntity(this: WorkspaceEntityActionsStore, categoryKey: string, title: string, fieldValues: Record<string, unknown> = {}, explorerParentId?: string | null, openAfterCreate = true) {
    if (!this.workspace) return;
    this.error = '';
    try {
      const entity = await api<EntityDocument>(`/api/v1/workspaces/${this.workspace.id}/free-entities`, {
        method: 'POST',
        body: JSON.stringify({ categoryKey, title, contentDoc: { type: 'doc', content: [] }, fieldValues, ...(explorerParentId !== undefined ? { explorerParentId } : {}) })
      });
      this.entities = [...this.entities.filter((item) => item.id !== entity.id), entity];
      if (openAfterCreate) {
        this.activeEntity = entity; this.backlinks = []; this.activeEntityTags = []; this.sceneSegments = []; this.sceneSegmentFields = []; this.sceneConflicts = [];
        history.pushState({}, '', `/w/${this.workspace.id}/e/${entity.id}`);
        void Promise.all([this.loadEntityTags(entity.id), this.loadBacklinks(entity.id)]);
      }
      void Promise.all([this.loadEntities(), this.loadCategoryCollections(), this.loadExplorer(), this.refreshConfiguration()]);
      return entity;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Freier Eintrag konnte nicht angelegt werden.';
      throw error;
    }
  },
  async setEntityProfileImage(this: WorkspaceEntityActionsStore, entityId: string, imageData: string | null) {
    const entity = await api<EntityDocument>(`/api/v1/entities/${entityId}/profile-image`, { method: 'PUT', body: JSON.stringify({ imageData }) });
    if (this.activeEntity?.id === entityId) this.activeEntity = entity;
    this.entities = this.entities.map((item) => item.id === entityId ? { ...item, profileImageData: entity.profileImageData } : item);
    return entity;
  },
  async uploadEntityAttachment(this: WorkspaceEntityActionsStore, entityId: string, file: File) {
    const attachment = await api<EntityAttachment>(`/api/v1/entities/${entityId}/attachments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name), 'X-File-Mime': file.type || 'application/octet-stream' },
      body: file
    });
    if (this.activeEntity?.id === entityId) this.activeEntity.attachments = [attachment, ...this.activeEntity.attachments];
    return attachment;
  },
  async removeEntityAttachment(this: WorkspaceEntityActionsStore, attachmentId: string) {
    await api(`/api/v1/attachments/${attachmentId}`, { method: 'DELETE' });
    if (this.activeEntity) this.activeEntity.attachments = this.activeEntity.attachments.filter((item) => item.id !== attachmentId);
  },
  async getDeleteImpact(this: WorkspaceEntityActionsStore, entityId: string) {
    return api<{ entity: { id: string; title: string; templateName: string; status: string }; references: Array<{ id: string; sourceEntityId: string; sourceTitle: string; sourceTemplateName: string; origin: string; fieldKey: string | null; sourcePath: string; labelSnapshot: string | null }> }>(`/api/v1/entities/${entityId}/delete-impact`);
  },
  async archiveEntity(this: WorkspaceEntityActionsStore, entityId: string, replacements?: Array<{ referenceId: string; replacementEntityId?: string | null }>) {
    await api(`/api/v1/entities/${entityId}/${replacements ? 'safe-delete' : 'delete'}`, { method: 'POST', ...(replacements ? { body: JSON.stringify({ replacements }) } : {}) });
    if (this.activeEntity?.id === entityId) { this.activeEntity = null; this.backlinks = []; this.sceneSegments = []; this.sceneSegmentFields = []; this.sceneConflicts = []; }
    await this.loadEntities(); await this.loadExplorer(); await this.loadTimeline();
    this.scheduleStoryTimelineRefresh();
  },
  async permanentlyDeleteEntity(this: WorkspaceEntityActionsStore, entityId: string) {
    await api(`/api/v1/entities/${entityId}/permanent-delete`, { method: 'POST' });
    if (this.activeEntity?.id === entityId) { this.activeEntity = null; this.backlinks = []; this.sceneSegments = []; this.sceneSegmentFields = []; this.sceneConflicts = []; }
    await this.loadEntities(); await this.loadCategoryCollections(); await this.loadExplorer(); await this.loadTimeline();
    this.scheduleStoryTimelineRefresh();
  }
};
